import React, { createContext, useContext, useState } from "react";
import { TTooltipStep, TOOLTIP_STEPS } from "../utils/constant";
import { mixpanel } from "../utils/mixpanel";
import { SessionContext } from ".";
import { useOnboardingSettings } from "./OnboardingSettingsContext";

type OnboardingTipsState = Partial<Record<TTooltipStep, boolean>>;

type OnboardingTipsContextType = {
  tipsState: OnboardingTipsState;
  markStepComplete: (step: TTooltipStep) => void;
  resetTips: () => void;
};

const OnboardingTipsContext = createContext<OnboardingTipsContextType>({
  tipsState: {},
  markStepComplete: () => {},
  resetTips: () => {},
});

export const OnboardingTipsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [tipsState, setTipsState] =
    useState<OnboardingTipsState>(defaultTipsState);
  const session = useContext(SessionContext)?.session;
  const {
    tourActive,
    showSimulateTips,
    showBeforeAfterTips,
    showConsultTips,
    showDeployWidgetTips,
  } = useOnboardingSettings();

  const isDeployWidgetStep = (stepId: TTooltipStep) =>
    stepId === TOOLTIP_STEPS.DEPLOY_WIDGET_TIPS_COMPLETED;

  const isSimulateStep = (stepId: TTooltipStep) =>
    stepId === TOOLTIP_STEPS.SELECT_PROCEDURE ||
    stepId === TOOLTIP_STEPS.SELECT_MODEL ||
    stepId === TOOLTIP_STEPS.SELECT_SECOND_PROCEDURE ||
    stepId === TOOLTIP_STEPS.SIMULATION_TIPS_COMPLETED;

  const isBeforeAfterStep = (stepId: TTooltipStep) =>
    stepId === TOOLTIP_STEPS.START_BEFORE_AFTER ||
    stepId === TOOLTIP_STEPS.USE_PLACEHOLDER_IMAGES ||
    stepId === TOOLTIP_STEPS.USE_AI_SETTINGS ||
    stepId === TOOLTIP_STEPS.GENERATE_IMAGES ||
    stepId === TOOLTIP_STEPS.EDIT_TEMPLATE ||
    stepId === TOOLTIP_STEPS.ADD_TEXT_OR_IMAGES ||
    stepId === TOOLTIP_STEPS.CONTINUE_TO_BEFORE_AFTER_RESULTS ||
    stepId === TOOLTIP_STEPS.ADJUST_IMAGES ||
    stepId === TOOLTIP_STEPS.SWAP_IMAGES ||
    stepId === TOOLTIP_STEPS.SAVE_CHANGES ||
    stepId === TOOLTIP_STEPS.DOWNLOAD_IMAGES ||
    stepId === TOOLTIP_STEPS.BEFORE_AFTER_TIPS_COMPLETED;

  const isConsultStep = (stepId: TTooltipStep) =>
    stepId === TOOLTIP_STEPS.START_CONSULT ||
    stepId === TOOLTIP_STEPS.SWITCH_VIEW ||
    stepId === TOOLTIP_STEPS.SELECT_APPOINTMENT ||
    stepId === TOOLTIP_STEPS.VIEW_APPOINTMENT_DETAIL ||
    stepId === TOOLTIP_STEPS.RECORD_CONSULT ||
    stepId === TOOLTIP_STEPS.VIEW_TRANSCRIPT ||
    stepId === TOOLTIP_STEPS.SELECT_IMAGE_TO_SIMULATE ||
    stepId === TOOLTIP_STEPS.SIMULATE_IN_CONSULT ||
    stepId === TOOLTIP_STEPS.CONSULT_TIPS_COMPLETED;

  const shouldTrackOnboardingTip = (stepId: TTooltipStep) => {
    if (!tourActive) return false;
    if (isDeployWidgetStep(stepId)) return showDeployWidgetTips;
    if (isSimulateStep(stepId)) return showSimulateTips;
    if (isBeforeAfterStep(stepId)) return showBeforeAfterTips;
    if (isConsultStep(stepId)) return showConsultTips;
    return false;
  };

  const markStepComplete = (stepId: TTooltipStep) => {
    /**
     * Marks a given onboarding step as complete and automatically progresses
     * to the next step in the relevant onboarding flow (Simulate or Before/After).
     *
     * ✅ Each onboarding step is represented as a boolean flag in `tipsState`.
     * ✅ When `markStepComplete(stepId)` is called:
     *    - The specified step is set to `true`
     *    - The next sequential step is also marked `true`, enabling highlight progression
     *
     * 🧠 Why we auto-progress:
     *    - This logic supports tooltip modals that visually highlight steps
     *    - Each screen component uses `tipsState` to decide which section to highlight
     *
     * 🌀 Onboarding flows:
     * - Simulate flow:
     *    SELECT_PROCEDURE ➜ SELECT_MODEL ➜ SELECT_SECOND_PROCEDURE ➜ SIMULATION_TIPS_COMPLETED
     *
     * - Before/After flow:
     *    START_BEFORE_AFTER ➜ USE_PLACEHOLDER_IMAGES ➜ USE_AI_SETTINGS ➜ GENERATE_IMAGES ➜
     *    SELECT_TEMPLATE ➜ EDIT_TEMPLATE ➜ ADJUST_IMAGES ➜ SWAP_IMAGES ➜
     *    SAVE_CHANGES ➜ DOWNLOAD_IMAGES ➜ BEFORE_AFTER_TIPS_COMPLETED
     *
     * ⚠️ Note: Tooltip highlights are driven by `!tipsState[current] && tipsState[previous]`
     *          So each step should only be marked complete *after* the user has interacted with it.
     *
     * 💡 markStepComplete is idempotent: calling it multiple times for the same step won't cause issues.
     */

    const shouldTrack = shouldTrackOnboardingTip(stepId);
    const isAlreadyComplete = Boolean(tipsState[stepId]);
    if (shouldTrack && !isAlreadyComplete) {
      mixpanel.track("Onboarding Tip Completed", {
        step: stepId,
        "User Email": session?.userEmail,
        clinic: session?.clinicName,
      });
    }

    setTipsState((prev) => {
      const updated = { ...prev, [stepId]: true };
      // Handle the transition to the next state

      // Simulation flow
      if (stepId === TOOLTIP_STEPS.SELECT_PROCEDURE) {
        updated[TOOLTIP_STEPS.SELECT_MODEL] = true;
      } else if (stepId === TOOLTIP_STEPS.SELECT_MODEL) {
        updated[TOOLTIP_STEPS.SELECT_SECOND_PROCEDURE] = true;
      } else if (stepId === TOOLTIP_STEPS.SELECT_SECOND_PROCEDURE) {
        // Set the completed flag instead of resetting
        updated[TOOLTIP_STEPS.SIMULATION_TIPS_COMPLETED] = true;
      }

      // Before/After flow
      else if (stepId === TOOLTIP_STEPS.START_BEFORE_AFTER) {
        updated[TOOLTIP_STEPS.USE_PLACEHOLDER_IMAGES] = true;
      } else if (stepId === TOOLTIP_STEPS.USE_PLACEHOLDER_IMAGES) {
        updated[TOOLTIP_STEPS.USE_AI_SETTINGS] = true;
      } else if (stepId === TOOLTIP_STEPS.USE_AI_SETTINGS) {
        updated[TOOLTIP_STEPS.GENERATE_IMAGES] = true;
      } else if (stepId === TOOLTIP_STEPS.GENERATE_IMAGES) {
        updated[TOOLTIP_STEPS.EDIT_TEMPLATE] = true;
      } else if (stepId === TOOLTIP_STEPS.EDIT_TEMPLATE) {
        updated[TOOLTIP_STEPS.ADD_TEXT_OR_IMAGES] = true;
      } else if (stepId === TOOLTIP_STEPS.ADD_TEXT_OR_IMAGES) {
        updated[TOOLTIP_STEPS.CONTINUE_TO_BEFORE_AFTER_RESULTS] = true;
      } else if (stepId === TOOLTIP_STEPS.CONTINUE_TO_BEFORE_AFTER_RESULTS) {
        updated[TOOLTIP_STEPS.ADJUST_IMAGES] = true;
      } else if (stepId === TOOLTIP_STEPS.ADJUST_IMAGES) {
        updated[TOOLTIP_STEPS.SWAP_IMAGES] = true;
      } else if (stepId === TOOLTIP_STEPS.SWAP_IMAGES) {
        updated[TOOLTIP_STEPS.SAVE_CHANGES] = true;
      } else if (stepId === TOOLTIP_STEPS.SAVE_CHANGES) {
        updated[TOOLTIP_STEPS.DOWNLOAD_IMAGES] = true;
      } else if (stepId === TOOLTIP_STEPS.DOWNLOAD_IMAGES) {
        updated[TOOLTIP_STEPS.BEFORE_AFTER_TIPS_COMPLETED] = true;
      }

      // Consult flow
      if (stepId === TOOLTIP_STEPS.START_CONSULT) {
        updated[TOOLTIP_STEPS.SWITCH_VIEW] = true;
      } else if (stepId === TOOLTIP_STEPS.SWITCH_VIEW) {
        updated[TOOLTIP_STEPS.SELECT_APPOINTMENT] = true;
      } else if (stepId === TOOLTIP_STEPS.SELECT_APPOINTMENT) {
        updated[TOOLTIP_STEPS.VIEW_APPOINTMENT_DETAIL] = true;
      } else if (stepId === TOOLTIP_STEPS.VIEW_APPOINTMENT_DETAIL) {
        updated[TOOLTIP_STEPS.RECORD_CONSULT] = true;
      } else if (stepId === TOOLTIP_STEPS.RECORD_CONSULT) {
        updated[TOOLTIP_STEPS.VIEW_TRANSCRIPT] = true;
      } else if (stepId === TOOLTIP_STEPS.VIEW_TRANSCRIPT) {
        updated[TOOLTIP_STEPS.SELECT_IMAGE_TO_SIMULATE] = true;
      } else if (stepId === TOOLTIP_STEPS.SELECT_IMAGE_TO_SIMULATE) {
        updated[TOOLTIP_STEPS.SIMULATE_IN_CONSULT] = true;
      } else if (stepId === TOOLTIP_STEPS.SIMULATE_IN_CONSULT) {
        updated[TOOLTIP_STEPS.CONSULT_TIPS_COMPLETED] = true;
        //   updated[TOOLTIP_STEPS.VIEW_SIMILAR_PATIENTS] = true;
        // } else if (stepId === TOOLTIP_STEPS.VIEW_SIMILAR_PATIENTS) {
        // updated[TOOLTIP_STEPS.CONSULT_TIPS_COMPLETED] = true;
      }

      return updated;
    });
  };

  const resetTips = () => setTipsState(defaultTipsState);

  return (
    <OnboardingTipsContext.Provider
      value={{ tipsState, markStepComplete, resetTips }}
    >
      {children}
    </OnboardingTipsContext.Provider>
  );
};

export const useOnboardingTips = () => useContext(OnboardingTipsContext);

export const defaultTipsState: OnboardingTipsState = {
  // Deploy Widget page
  deployWidgetTipsCompleted: false,

  // Simulate page
  selectProcedure: true,
  selectModel: false,
  selectSecondProcedure: false,
  simulationTipsCompleted: false,

  // Before/After
  startBeforeAfter: true,
  usePlaceholderImages: false,
  useAISettings: false,
  generateImages: false,
  editTemplate: false,
  addTextOrImages: false,
  continueToBeforeAfterResults: false,
  adjustImages: false,
  swapImages: false,
  saveChanges: false,
  downloadImages: false,
  beforeAfterTipsCompleted: false,

  // Consults
  startConsult: true,
  switchView: false,
  selectAppointment: false,
  viewAppointmentDetail: false,
  recordConsult: false,
  viewTranscript: false,
  selectImageToSimulate: false,
  simulateInConsult: false,
  // viewSimilarPatients: false,
  consultTipsCompleted: false,
};
